export const TABS = [
  "overview", "live", "audience", "chat", "games", "watch-parties", "moments", "second-screen", "safety", "tenants",
] as const;
export type TabKey = (typeof TABS)[number];

export const PRESETS = ["24h", "7d", "30d", "90d", "custom"] as const;
export type Preset = (typeof PRESETS)[number];

export interface DashboardFilters {
  tab: TabKey;
  /** Empty means every tenant. */
  tenants: string[];
  preset: Preset;
  /** Inclusive local dates, YYYY-MM-DD; only used when preset is "custom". */
  from: string;
  to: string;
  /** Empty means any game. */
  gameId: string;
}

export const DEFAULT_FILTERS: DashboardFilters = {
  tab: "overview", tenants: [], preset: "7d", from: "", to: "", gameId: "",
};

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

function oneOf<T extends string>(values: readonly T[], value: string | null): value is T {
  return value !== null && (values as readonly string[]).indexOf(value) >= 0;
}

export function parseFilters(search: string): DashboardFilters {
  const q = new URLSearchParams(search);
  const tab = q.get("tab");
  const range = q.get("range");
  const from = q.get("from") ?? "";
  const to = q.get("to") ?? "";
  const custom = range === "custom" && DATE.test(from) && DATE.test(to) && from <= to;
  const preset: Preset = custom ? "custom" : oneOf(PRESETS, range) && range !== "custom" ? range : DEFAULT_FILTERS.preset;
  return {
    tab: oneOf(TABS, tab) ? tab : DEFAULT_FILTERS.tab,
    tenants: (q.get("tenant") ?? "").split(",").map((t) => t.trim()).filter((t) => t.length > 0),
    preset,
    from: custom ? from : "",
    to: custom ? to : "",
    gameId: (q.get("gameId") ?? "").trim(),
  };
}

export function serializeFilters(f: DashboardFilters): string {
  const q = new URLSearchParams();
  if (f.tab !== DEFAULT_FILTERS.tab) q.set("tab", f.tab);
  if (f.tenants.length > 0) q.set("tenant", f.tenants.join(","));
  if (f.preset !== DEFAULT_FILTERS.preset) q.set("range", f.preset);
  if (f.preset === "custom") {
    q.set("from", f.from);
    q.set("to", f.to);
  }
  if (f.gameId) q.set("gameId", f.gameId);
  return q.toString();
}

/** YYYY-MM-DD of `d` in the browser's own zone. */
export function localDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Calendar arithmetic on a YYYY-MM-DD date (no time zone involved). */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/**
 * The range to ask the AL for. Dates are local days (the AL reads them in
 * the tz it is sent); `to` is exclusive, so "last 7 days" ends tomorrow.
 * The last 24 hours are exact instants, which the AL buckets by hour.
 */
export function rangeFor(f: DashboardFilters, now: Date): { from: string; to: string } {
  if (f.preset === "24h") {
    return { from: new Date(now.getTime() - DAY_MS).toISOString(), to: now.toISOString() };
  }
  if (f.preset === "custom") return { from: f.from, to: addDays(f.to, 1) };
  const days = f.preset === "7d" ? 7 : f.preset === "30d" ? 30 : 90;
  const tomorrow = addDays(localDate(now), 1);
  return { from: addDays(tomorrow, -days), to: tomorrow };
}

export function statsQuery(f: DashboardFilters, now: Date, tz: string, opts: { range?: boolean } = {}): string {
  const q = new URLSearchParams();
  if (f.tenants.length > 0) q.set("tenant", f.tenants.join(","));
  if (f.gameId) q.set("gameId", f.gameId);
  q.set("tz", tz);
  if (opts.range !== false) {
    const r = rangeFor(f, now);
    q.set("from", r.from);
    q.set("to", r.to);
  }
  return q.toString();
}
